import { useState, useEffect, useRef, memo } from 'react';
import GaugeChart from 'react-gauge-chart';
import { Box, Typography, Tooltip } from '@mui/material';
import { formatNumber } from '../utils/formatNumber';

const GaugeMeter2 = memo(({
  label,
  min,
  max,
  value,
  target,
  scaleMin,
  scaleMax,
  descriptionHtml
}) => {
  // const isMobile = useMediaQuery('(max-width:600px)');
  const isFirstRender = useRef(true);

  // Estado para el ancho y escala
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 700
  );

  useEffect(() => {
    // Handler para resize
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    // Limpieza
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Marcar después del primer render
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  const scale = windowWidth < 700 ? windowWidth / 700 : 1;

  const clamp01 = (n) => Math.max(0, Math.min(1, n));

  // Normalizar el valor para react-gauge-chart (0-1) con validaciones para evitar NaN
  const numericValue = Number(value);
  const numericMin = Number(min);
  const numericMax = Number(max);
  const numericScaleMin = Number(scaleMin);
  const numericScaleMax = Number(scaleMax);
  const denom = numericScaleMax - numericScaleMin;
  const hasValidScale = Number.isFinite(denom) && denom !== 0;
  const safeMin = Number.isFinite(numericMin) ? numericMin : numericScaleMin;
  const safeMax = Number.isFinite(numericMax) ? numericMax : numericScaleMax;
  const normValue = hasValidScale
    ? (numericValue - numericScaleMin) / denom
    : 0;
  const percent = Number.isFinite(normValue)
    ? clamp01(normValue)
    : 0;

  // Colores de los arcos
  /*const arcColors = [
    '#EA4228', // rojo
    '#4CAF50', // verde
    '#EA4228'  // rojo
  ];*/
  // Porcentajes de los arcos
  /*const arcDelimiters = [
    (min - scaleMin) / (scaleMax - scaleMin),
    (max - scaleMin) / (scaleMax - scaleMin) - (min - scaleMin) / (scaleMax - scaleMin),
    1 - (max - scaleMin) / (scaleMax - scaleMin)
  ];*/

  let _label = label || '';
  _label = _label.replace(/_/g, ' ');
  _label = _label.charAt(0).toUpperCase() + _label.slice(1);

  // Nuevo: aplicar escala a los tamaños
  const gaugeWidth = 135 * scale;
  const boxWidth = windowWidth > 1024 ? '150px' : '19%';
  
  // Redondear el valor para la comparación de color
  const roundedValue = parseFloat(formatNumber(value));
  const valueColor = roundedValue > max ? '#EA4228' : roundedValue < min ? '#EA4228' : '#4CAF50';
  const labelFontSize = 14 * scale;
  const valueFontSize = 18 * scale;
  const needleFontSize = 11 * scale;

  const gaugeId = `${label || 'gauge'}-${numericScaleMin}-${numericScaleMax}`;
  const gaugeKey = `${gaugeId}-${percent}`; // force remount when percent changes to avoid stale needles

  const arc1 = hasValidScale ? clamp01((safeMin - numericScaleMin) / denom) : 0;
  const maxPos = hasValidScale ? clamp01((safeMax - numericScaleMin) / denom) : 1;
  const arc2 = hasValidScale ? clamp01(maxPos - arc1) : 1;
  const arc3 = hasValidScale ? clamp01(1 - (arc1 + arc2)) : 0;

  const content = (
    <>
      <GaugeChart
        key={gaugeKey}
        id={gaugeId}
        nrOfLevels={420}
        arcsLength={[
          arc1,
          arc2,
          arc3
        ]}
        colors={['#EA4228', '#4CAF50', '#EA4228']}
        percent={percent}
        arcPadding={0.02}
        cornerRadius={0}
        needleColor="#000"
        needleBaseColor="#000"
        textColor="#717171"
        animate={isFirstRender.current}
        animDelay={0}
        style={{ width: gaugeWidth, margin: '0 auto', height: gaugeWidth * 0.5 }}
        hideText={true}
      />
      <Typography variant="subtitle1" sx={{ textAlign: 'center', fontWeight: 700, fontSize: valueFontSize, color: valueColor, mt: -1 }}>
        {formatNumber(value)}
      </Typography>
      <Typography variant="h6" sx={{ textAlign: 'center', lineHeight: '1.1', fontSize: labelFontSize, fontWeight: 700, color: '#717171', mb: 0 }}>
        {_label}
      </Typography>
        {(min !== undefined && min !== null && max !== undefined && max !== null) && (
          <Typography variant="body2" sx={{ fontWeight: 400, textAlign: 'center', opacity: 0.5, fontSize: needleFontSize }}>
            {formatNumber(min)} - {formatNumber(max)}
          </Typography>
        )}
    </>
  );

  return descriptionHtml ? (
    <Tooltip
      title={<span dangerouslySetInnerHTML={{ __html: descriptionHtml }} />}
      enterTouchDelay={50}
      leaveTouchDelay={3000}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -30],
              },
            },
          ],
        },
      }}
    >
      <span style={{ display: 'inline-block', width: boxWidth, textAlign: 'center' }}>
        <Box>{content}</Box>
      </span>
    </Tooltip>
  ) : (
    <Box sx={{ width: boxWidth }}>{content}</Box>
  );
  });

GaugeMeter2.displayName = 'GaugeMeter2';

export default GaugeMeter2;
